import App from 'AppInterface/App';
import firebase from 'fb/initconfig';
import AnandDate from 'helpers/AnandDate';
import { BulkChangePlan, BulkChangeRow, BulkFieldChange } from 'helpers/bulkEdit/changePlan';
import { toCsv, UTF8_BOM } from 'helpers/csv';
import { normalizeMobileNumber } from 'helpers/mobileNumber';
import { capitalizeName } from 'helpers/nameCase';
import { parseDateTime } from 'helpers/parseDateTime';

/**
 * Export and import of Anumati records as CSV.
 *
 * An import is expressed as an ordinary change plan, so it reuses the bulk edit preview,
 * per record progress and undo without any of that having to know about files.
 */

const DATE_FORMAT = 'YYYY-MM-DD';
/** Round trips through `parseDateTime`, so an exported file re-imports unchanged. */
const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export const ID_COLUMN = 'id';
export const CARD_COLUMN = 'Card No';

export type ImportMissingAction = 'leave' | 'softDelete';

interface ImportColumn {
	header: string;
	field: string;
	label: string;
	/** Renders the stored value for the file and for the preview. */
	format: (value: any) => string;
	/** Turns a cell back into a stored value. Returning undefined means "cannot read this". */
	parse: (text: string) => any;
	/** Compares a parsed value with the stored one, since timestamps are not plain equals. */
	equals: (storedValue: any, parsedValue: any) => boolean;
}

function textColumn(header: string, field: string, normalise: (value: string) => string = (value) => value): ImportColumn {
	return {
		header,
		field,
		label: header,
		format: (value) => (value === null || value === undefined ? '' : String(value)),
		parse: (text) => normalise(text.trim()),
		equals: (stored, parsed) => (stored ?? '') === (parsed ?? ''),
	};
}

const countColumn: ImportColumn = {
	header: 'Count',
	field: 'people_count',
	label: 'Count',
	format: (value) => (value === null || value === undefined ? '' : String(value)),
	parse: (text) => {
		if (!/^\d+$/.test(text.trim())) {
			return undefined;
		}
		return parseInt(text.trim(), 10);
	},
	equals: (stored, parsed) => stored === parsed,
};

const dateColumn: ImportColumn = {
	header: 'Date',
	field: 'permission_date',
	label: 'Date',
	format: (value) => (value ? String(value) : ''),
	parse: (text) => {
		let trimmed = text.trim();
		if (!trimmed || isNaN(new Date(trimmed).getTime())) {
			return undefined;
		}
		return new AnandDate(trimmed).format(DATE_FORMAT);
	},
	equals: (stored, parsed) => (stored ?? '') === (parsed ?? ''),
};

function timestampColumn(header: string, field: string): ImportColumn {
	return {
		header,
		field,
		label: header,
		format: (value) => (value?.seconds ? new AnandDate(value).format(DATE_TIME_FORMAT) : ''),
		parse: (text) => {
			let parsed = parseDateTime(text.trim());
			return parsed ? firebase.firestore.Timestamp.fromDate(parsed) : undefined;
		},
		equals: (stored, parsed) => (stored?.seconds ?? null) === (parsed?.seconds ?? null),
	};
}

/**
 * The editable columns. Identity columns are handled separately: they are written to the
 * file so rows can be matched back, but never imported.
 */
export const editableColumns: ImportColumn[] = [
	textColumn('Name', 'name', (value) => capitalizeName(value) ?? ''),
	countColumn,
	dateColumn,
	textColumn('Ref', 'reference'),
	textColumn('Mobile', 'phone', (value) => (value ? normalizeMobileNumber(value) ?? value : '')),
	timestampColumn('Publish Time', 'publishTime'),
	timestampColumn('Expire Time', 'expireTime'),
];

interface ReadOnlyColumn {
	header: string;
	value: (record: any) => string;
}

function yesNo(value: any) {
	return value ? 'Yes' : 'No';
}

/** Handles both Firestore timestamps and the plain strings some records carry. */
function anyTimeToText(value: any) {
	if (!value) {
		return '';
	}

	if (value.seconds) {
		return new AnandDate(value).format(DATE_TIME_FORMAT);
	}

	return String(value);
}

function checkinEntries(record: any): any[] {
	return Array.isArray(record?.checkin) ? record.checkin : [];
}

/** Mirrors the tile's count: everyone actually checked in, across all four categories. */
function checkedInPeople(record: any) {
	return checkinEntries(record).reduce((total, entry) => {
		if (!entry || entry.in_out !== 'in') {
			return total;
		}

		return (
			total +
			(parseInt(entry.checkin_count ?? '0') || 0) +
			(parseInt(entry.checkin_count_ladies ?? '0') || 0) +
			(parseInt(entry.checkin_count_minor ?? '0') || 0) +
			(parseInt(entry.checkin_count_infant ?? '0') || 0)
		);
	}, 0);
}

function describeCheckins(record: any) {
	return checkinEntries(record)
		.map((entry) => {
			let direction = entry?.in_out === 'out' ? 'Out' : 'In';
			let counts = [entry?.checkin_count ?? 0, entry?.checkin_count_ladies ?? 0, entry?.checkin_count_minor ?? 0, entry?.checkin_count_infant ?? 0].join('/');

			return [
				`[${direction}]`,
				entry?.checkin_name ?? '',
				counts,
				entry?.checkin_phone ?? '',
				entry?.checkin_city ?? '',
				entry?.checkin_conveyance ?? '',
				anyTimeToText(entry?.created_at),
			]
				.map((part) => String(part).trim())
				.join(' · ');
		})
		.join(' | ');
}

/**
 * Context columns. They are exported so a sheet is a complete picture of a record, and are
 * ignored on import because they are either derived, system owned, or structured data that
 * cannot round trip through a single cell.
 */
export const readOnlyColumns: ReadOnlyColumn[] = [
	{ header: 'Place', value: (record) => record.place ?? '' },
	{ header: 'Approval', value: (record) => record.approval?.status ?? '' },
	{ header: 'Card Printed', value: (record) => yesNo(record.card_printed) },
	{ header: 'Deleted', value: (record) => yesNo(record.deleted) },
	{ header: 'Status', value: (record) => record.status ?? '' },
	{ header: 'Rahbar', value: (record) => record.rahbar ?? '' },
	{ header: 'Rahbar Title', value: (record) => record.rahbar_title ?? '' },
	{ header: 'SMS State', value: (record) => record.sms?.state ?? '' },
	{ header: 'SMS Reported At', value: (record) => anyTimeToText(record.sms?.reportAt) },
	{ header: 'AMS State', value: (record) => record.ams?.state ?? '' },
	{ header: 'AMS Error', value: (record) => record.ams?.error ?? '' },
	{ header: 'Check-In Entries', value: (record) => String(checkinEntries(record).length) },
	{ header: 'Checked-In People', value: (record) => String(checkedInPeople(record)) },
	{
		header: 'Check-In Details (In/Out · Name · Adults/Ladies/Minors/Infants · Mobile · City · Conveyance · Time)',
		value: describeCheckins,
	},
	{ header: 'Created At', value: (record) => anyTimeToText(record.createdAt ?? record.created_at) },
	{ header: 'Created By', value: (record) => record.byName ?? '' },
	{ header: 'Last Updated At', value: (record) => anyTimeToText(record.updatedAt) },
];

export const exportHeaders = [
	ID_COLUMN,
	CARD_COLUMN,
	...editableColumns.map((column) => column.header),
	...readOnlyColumns.map((column) => column.header),
];

/** Every header the importer recognises, so only genuine typos are flagged. */
export const knownHeaders = new Set(exportHeaders);

export function recordsToCsv(records: any[]): string {
	let rows: any[][] = [exportHeaders];

	(records ?? []).forEach((record) => {
		rows.push([
			record?.id ?? '',
			record?.card_no ?? '',
			...editableColumns.map((column) => column.format(record?.[column.field])),
			...readOnlyColumns.map((column) => column.value(record ?? {})),
		]);
	});

	return UTF8_BOM + toCsv(rows);
}

export function exportFileName(prefix = 'anumati') {
	return `${prefix}-export-${new AnandDate().format('YYYY-MM-DD-HHmm')}.csv`;
}

/**
 * Hands the CSV to the device. The app bridge is used so this also works inside the native
 * wrapper, where an anchor download would be ignored.
 */
export function downloadCsv(csv: string, fileName: string) {
	let blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
	let url = URL.createObjectURL(blob);

	try {
		App.downloadFile(url, fileName);
	} finally {
		// The bridge reads the blob synchronously, and Safari revokes it itself.
		setTimeout(() => URL.revokeObjectURL(url), 10000);
	}
}

export interface ImportParseResult {
	plan: BulkChangePlan | null;
	error?: string;
	/** Columns in the file that are not understood, surfaced so typos are obvious. */
	unknownHeaders: string[];
	matchedCount: number;
	notFoundCount: number;
	missingFromFileCount: number;
}

function cellChanged(column: ImportColumn, record: any, text: string): BulkFieldChange | null | undefined {
	// A blank cell means "leave this field alone" rather than "clear it", so a partially
	// filled sheet cannot silently wipe data.
	if (text === '') {
		return null;
	}

	let parsed = column.parse(text);

	if (parsed === undefined) {
		return undefined;
	}

	let current = record[column.field];

	if (column.equals(current, parsed)) {
		return null;
	}

	return {
		field: column.field,
		label: column.label,
		currentValue: current ?? null,
		newValue: parsed,
		currentLabel: column.format(current) || '—',
		newLabel: column.format(parsed) || '—',
	};
}

/**
 * Builds the change plan for an imported file.
 *
 * Only records present in the file are edited. Records missing from the file are listed so
 * the outcome is visible, and are left untouched unless the caller asks otherwise.
 */
export function buildImportPlan(
	records: any[],
	fileRows: { [key: string]: string }[],
	headers: string[],
	missingAction: ImportMissingAction
): ImportParseResult {
	let unknownHeaders = headers.filter((header) => header && !knownHeaders.has(header));

	let usableColumns = editableColumns.filter((column) => headers.includes(column.header));

	if (!headers.includes(ID_COLUMN) && !headers.includes(CARD_COLUMN)) {
		return {
			plan: null,
			error: `The file needs an "${ID_COLUMN}" or "${CARD_COLUMN}" column so rows can be matched to records.`,
			unknownHeaders,
			matchedCount: 0,
			notFoundCount: 0,
			missingFromFileCount: 0,
		};
	}

	if (!usableColumns.length) {
		return {
			plan: null,
			error: 'The file has no editable columns. Export a file first and edit that.',
			unknownHeaders,
			matchedCount: 0,
			notFoundCount: 0,
			missingFromFileCount: 0,
		};
	}

	let byId = new Map<string, any>();
	let byCard = new Map<string, any>();

	(records ?? []).forEach((record) => {
		if (record?.id) {
			byId.set(String(record.id), record);
		}
		if (record?.card_no !== undefined && record?.card_no !== null && record.card_no !== '') {
			byCard.set(String(record.card_no), record);
		}
	});

	let rows: BulkChangeRow[] = [];
	let seen = new Set<string>();
	let matchedCount = 0;
	let notFoundCount = 0;

	fileRows.forEach((fileRow, index) => {
		let id = (fileRow[ID_COLUMN] ?? '').trim();
		let cardNo = (fileRow[CARD_COLUMN] ?? '').trim();
		let record = (id && byId.get(id)) || (cardNo && byCard.get(cardNo));

		if (!record) {
			notFoundCount++;
			rows.push({
				id: `missing-row-${index}`,
				record: { card_no: cardNo || '—', name: fileRow['Name'] ?? '', reference: fileRow['Ref'] ?? '' },
				status: 'blocked',
				reason: 'No matching record',
				currentValue: null,
				currentLabel: '—',
				newLabel: '—',
				group: 'notFound',
			});
			return;
		}

		seen.add(String(record.id));
		matchedCount++;

		let changes: BulkFieldChange[] = [];
		let unreadable: string[] = [];

		usableColumns.forEach((column) => {
			let change = cellChanged(column, record, (fileRow[column.header] ?? '').trim());

			if (change === undefined) {
				unreadable.push(column.header);
			} else if (change) {
				changes.push(change);
			}
		});

		if (unreadable.length) {
			rows.push({
				id: String(record.id),
				record,
				status: 'blocked',
				reason: `Could not read ${unreadable.join(', ')}`,
				currentValue: null,
				currentLabel: '—',
				newLabel: '—',
				group: 'matched',
			});
			return;
		}

		rows.push({
			id: String(record.id),
			record,
			status: changes.length ? 'change' : 'unchanged',
			reason: changes.length ? undefined : 'Already matches the file',
			currentValue: null,
			currentLabel: '—',
			newLabel: '—',
			changes: changes.length ? changes : undefined,
			group: 'matched',
		});
	});

	// Records the file does not mention. Listed either way so their fate is never a surprise.
	let missingFromFile = (records ?? []).filter((record) => record?.id && !seen.has(String(record.id)));

	missingFromFile.forEach((record) => {
		if (missingAction === 'softDelete' && !record.deleted) {
			rows.push({
				id: String(record.id),
				record,
				status: 'change',
				currentValue: null,
				currentLabel: '—',
				newLabel: '—',
				group: 'missingFromFile',
				changes: [
					{
						field: 'deleted',
						label: 'Deleted',
						currentValue: !!record.deleted,
						newValue: true,
						currentLabel: 'Not deleted',
						newLabel: 'Deleted',
					},
				],
			});
			return;
		}

		rows.push({
			id: String(record.id),
			record,
			status: 'unchanged',
			reason: 'Not in the file, left as it is',
			currentValue: null,
			currentLabel: '—',
			newLabel: '—',
			group: 'missingFromFile',
		});
	});

	let plan: BulkChangePlan = {
		field: 'import',
		fieldLabel: 'Imported file',
		operation: 'set',
		operationLabel: 'Import',
		input: '',
		destructive: false,
		rows,
		changeCount: rows.filter((row) => row.status === 'change').length,
		unchangedCount: rows.filter((row) => row.status === 'unchanged').length,
		blockedCount: rows.filter((row) => row.status === 'blocked').length,
	};

	return {
		plan,
		unknownHeaders,
		matchedCount,
		notFoundCount,
		missingFromFileCount: missingFromFile.length,
	};
}
